import dynamic from 'next/dynamic';

import type { LOTTIE_ANIMATION } from '@commons/components/LottiePlayer/animations';
import type { DrupalImage as DrupalImageType, DrupalVideo } from '@commons/types/drupal';

// Split background types into separate chunks to avoid loading them when not needed.

const Texture = dynamic(() => import('@commons/components/Texture'));
const LottiePlayer = dynamic(() => import('@commons/components/LottiePlayer'));
const MediaBackground = dynamic(() => import('./Media'));
const ParallaxBackground = dynamic(() => import('./Parallax'));

// Type definition for the background settings returned by Drupal.

export type BackgroundSettings = {
  backgroundType: 'none' | 'media' | 'texture' | 'lottie';
  animatedTexture?: boolean;
  lottieAnimation?: LOTTIE_ANIMATION;
  parallaxEffect?: boolean;
  backgroundMedia?: DrupalImageType | DrupalVideo;
  mobileBackgroundMedia?: DrupalImageType | DrupalVideo;
  overlayOpacity?: number;
};

// Type definition for the Background component properties.

type Props = BackgroundSettings & {
  priority?: boolean;
};

/**
 * Background component.
 *
 * Used in multiple sections to render a background texture or media.
 * They should all share the same payload, which is why the BackgroundSettings type is exported here.
 */

export const Background = (props: Props) => {
  const {
    backgroundType,
    animatedTexture,
    lottieAnimation,
    backgroundMedia,
    mobileBackgroundMedia,
    overlayOpacity,
    priority = false,
  } = props;

  if (backgroundType === 'texture') {
    return <Texture animated={animatedTexture} />;
  }

  if (backgroundType === 'lottie' && lottieAnimation) {
    return <LottiePlayer animation={lottieAnimation} />;
  }

  if (backgroundType === 'media' && backgroundMedia) {
    const renderedMedia = (
      <MediaBackground
        media={backgroundMedia}
        mobileMedia={mobileBackgroundMedia}
        overlayOpacity={overlayOpacity}
        priority={priority}
      />
    );

    const hasParallax = props.parallaxEffect ?? false;
    if (hasParallax) {
      return <ParallaxBackground>{renderedMedia}</ParallaxBackground>;
    }

    return renderedMedia;
  }
};

export default Background;
